import { motion } from 'framer-motion';
import React from 'react';
import { FiArrowRight, FiCalendar, FiClock, FiUser } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const BlogsSection = () => {
  const blogs = [
    {
      id: 1,
      title: "The Future of AI in Enterprise: Transforming Business Operations",
      excerpt: "Discover how artificial intelligence is revolutionizing enterprise operations, from automated workflows to predictive analytics that drive strategic decision-making.",
      author: "Sarah Johnson",
      date: "December 15, 2024",
      readTime: "8 min read",
      category: "Artificial Intelligence",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop&crop=entropy&auto=format",
      gradient: "from-purple-500 to-pink-600"
    },
    {
      id: 2,
      title: "Cloud Migration Strategies: Best Practices for Modern Enterprises",
      excerpt: "Learn the essential strategies and methodologies for successful cloud migration, including security considerations and cost optimization techniques.",
      author: "Michael Chen",
      date: "December 12, 2024",
      readTime: "6 min read",
      category: "Cloud Infrastructure",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=400&fit=crop&crop=entropy&auto=format",
      gradient: "from-blue-500 to-indigo-600"
    },
    {
      id: 3,
      title: "Cybersecurity in 2024: Emerging Threats and Defense Strategies",
      excerpt: "Stay ahead of evolving cyber threats with comprehensive security frameworks and proactive defense mechanisms tailored for modern businesses.",
      author: "David Rodriguez",
      date: "December 10, 2024",
      readTime: "10 min read",
      category: "Cybersecurity",
      image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=600&h=400&fit=crop&crop=entropy&auto=format",
      gradient: "from-red-500 to-orange-600"
    },
    {
      id: 4,
      title: "Building Agile Development Teams: A Complete Guide to Success",
      excerpt: "Explore proven methodologies for assembling high-performing agile teams that deliver exceptional results in fast-paced development environments.",
      author: "Emily Thompson",
      date: "December 8, 2024",
      readTime: "7 min read",
      category: "Project Management",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop&crop=entropy&auto=format",
      gradient: "from-green-500 to-teal-600"
    }
  ];

  return (
    <section id="blogs" className="py-20 bg-gradient-to-br from-gray-50 to-slate-100 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-0 w-64 h-64 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-1/4 right-0 w-64 h-64 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium mb-4 font-accent tracking-wide"
          >
            Latest Insights
          </motion.span>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight"
          >
            From Our
            <span className="block bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Technology Blogs
            </span>
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            viewport={{ once: true }}
            className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed"
          >
            Stay updated with the latest trends, insights, and best practices in technology, 
            digital transformation, and business innovation from our expert team.
          </motion.p>
        </motion.div>

        {/* Featured Blog */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
            <div className="lg:grid lg:grid-cols-2">
              {/* Featured Image */}
              <div className="relative h-64 lg:h-full">
                <img
                  src={blogs[0].image}
                  alt={blogs[0].title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent"></div>
                <div className="absolute top-6 left-6">
                  <span className={`px-3 py-1 bg-gradient-to-r ${blogs[0].gradient} text-white text-sm font-medium rounded-full font-accent tracking-wide`}>
                    Featured
                  </span>
                </div>
              </div>

              {/* Featured Content */}
              <div className="p-8 lg:p-12">
                <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4 font-body">
                  <div className="flex items-center space-x-1">
                    <FiCalendar size={16} />
                    <span>{blogs[0].date}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <FiClock size={16} />
                    <span>{blogs[0].readTime}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <FiUser size={16} />
                    <span>{blogs[0].author}</span>
                  </div>
                </div>

                <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4 font-display tracking-tight leading-tight">
                  {blogs[0].title}
                </h3>
                
                <p className="text-gray-600 mb-6 font-body leading-relaxed">
                  {blogs[0].excerpt}
                </p>

                <Link to={`/blog/${blogs[0].id}`}>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 font-accent tracking-wide"
                  >
                    <span>Read Full Article</span>
                    <FiArrowRight />
                  </motion.button>
                </Link>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {blogs.slice(1).map((blog, index) => (
            <motion.div
              key={blog.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              viewport={{ once: true }}
              whileHover={{ y: -10, scale: 1.02 }}
              className="group cursor-pointer"
            >
              <Link to={`/blog/${blog.id}`}>
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100">
                  {/* Blog Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={blog.image}
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className={`px-3 py-1 bg-gradient-to-r ${blog.gradient} text-white text-xs font-medium rounded-full font-accent tracking-wide`}>
                        {blog.category}
                      </span>
                    </div>
                  </div>

                  {/* Blog Content */}
                  <div className="p-6">
                    <div className="flex items-center space-x-3 text-xs text-gray-500 mb-3 font-body">
                      <div className="flex items-center space-x-1">
                        <FiCalendar size={14} />
                        <span>{blog.date}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <FiClock size={14} />
                        <span>{blog.readTime}</span>
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-indigo-600 transition-colors duration-300 font-heading tracking-wide leading-tight">
                      {blog.title}
                    </h3>
                    
                    <p className="text-gray-600 text-sm mb-4 font-body leading-relaxed line-clamp-3">
                      {blog.excerpt}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-medium font-body">
                            {blog.author.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <span className="text-sm text-gray-600 font-body">{blog.author}</span>
                      </div>
                      
                      <motion.div
                        whileHover={{ x: 5 }}
                        className="text-indigo-600 group-hover:text-indigo-700"
                      >
                        <FiArrowRight size={18} />
                      </motion.div>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* View All Blogs CTA */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Link to="/blogs">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-10 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 font-accent tracking-wide"
            >
              View All Blog Posts
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default BlogsSection; 